// Library Imports
import * as React from "react";
import {
  Grid, InputLabel, Switch, TextField, MenuItem, FormControl, Select, Link, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

// helper imports
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";

// header import
import { getHeaders } from "../utils/HeaderHelper";

// app setting imports
import { AuthenticationContext, ImagesContext } from "../Context";
import CustomDatePicker from "../common/CustomDatePicker";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/Delete";
import { DRUG_PROBLEM_RESOLUTIONS, DRUG_THERAPY_PROBLEMS } from "../globals/globals";
import { useFlash } from "../FlashContext";

interface Props {
  encounterType: string;
  setEncounterType: any;
  placeOfService: string;
  placeOfServiceList: any;
  setPlaceOfService: any;
  encounterTypeOptions: any;
  readOnly: boolean;
  medicationProblems: any;
  setMedicationProblems: any;
}

const EncounterMedicationProblemsPanel: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);
  const [medicationSearch, setMedicationSearch] = React.useState<any>(null);
  const [lastMedicationId, setLastMedicationId] = React.useState<any>(0);
  const images = React.useContext(ImagesContext);
  const searchTimeout = React.useRef(null)
  const { showToast } = useFlash();

  React.useEffect(() => {
    setLastMedicationId(
      Math.max(...(props.medicationProblems?.length > 0 ? props.medicationProblems?.map(item => item.key) : [1]))
    );
  }, [props.medicationProblems])

  React.useEffect(() => {   
  }, [medicationSearch])

  const searchMedication = (key, name) => {
    clearTimeout(searchTimeout.current); //typing delay
    
    searchTimeout.current = setTimeout(() => {
      fetch(`/medication_search`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          medication: {
            name: name,
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            console.log(result.error)
            showToast({
              title: "Error Searching Medication",
              text: "Something went wrong.",
              type: "error",
            });
            setMedicationSearch(null);
          } else {
            let medicationSearchObject = result.data.results;
            // console.log("medicationSearchObject", medicationSearchObject);
            setMedicationSearch({ key: key, results: medicationSearchObject });
          }
        })
        .catch((error) => {
          console.log(error)
          showToast({
            title: "Error Searching Medication",
            text: "Something went wrong.",
            type: "error",
          });
          setMedicationSearch(null);
        });
    }, 250) 
  }

  const addNewProblem = () => {
    props.setMedicationProblems((prev) => { return [...prev, { key: lastMedicationId + 1, medication_name: null, type_of_problem: null, resolution: null }] });
    setLastMedicationId(lastMedicationId + 1)
  }

  return (
    <Grid container item xs={12} className="single-panel medication-related-problems">
      <Grid item xs={12}>
        <Grid container direction="row" className="admin-header bottom-border-orange">
          <Grid item xs={12} lg={6}>
            <h3>Medication Related Problems</h3>
          </Grid>

          {!props.readOnly &&
            <Link className="action-link row" onClick={addNewProblem}>
              <img
                src={images.add_icon}
                alt="Add Insurance"
                className="action-icon-image"
                style={{ width: 31.466, maxHeight: 32 }}
              />
                <span className="app-user-text disable-pointer" style={{marginLeft: '8px'}}>Add Additional Medication Related Problem</span>
            </Link>
          }
        </Grid>

        <Grid container 
        >
          <TableContainer 
            style={{ 
              // maxHeight: 300
              overflow: 'visible'
            }}
          >
            <Table stickyHeader className="no-border-table">
              <TableHead className="table-header-box">
                <TableRow>
                  <TableCell className="nowrap-header">
                    <p className="table-header">Medication Involved</p>
                  </TableCell>
                  <TableCell className="nowrap-header">
                    <p className="table-header">Type Of Drug Therapy Problem</p>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <p className="table-header" style={{ textAlign: 'left' }}>Resolution</p>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="form-body">
                {
                  props.medicationProblems?.length == 0 ?
                    <span style={{textAlign: 'center', marginTop: '10px'}}> </span>
                    :
                    (props.medicationProblems.map((problem, index) => {
                     return (
                      <TableRow key={index}>
                        <TableCell>
                          <Grid item xs={12} className="field-container">
                            <TextField
                              id="medication_name"
                              // label="Medication Name*"
                              value={problem.medication_name}
                              className="field w-100"
                              required
                              variant="outlined"
                              onChange={(event) => {
                                props.setMedicationProblems((prev)=> { 
                                  return prev.map((p) =>
                                    p.key === problem.key
                                      ? { ...p, medication_name: event.target.value }
                                      : p
                                  )
                                })
                                searchMedication(problem.key, event.target.value);
                              }}
                              InputLabelProps={{
                                required: false,
                              }}
                              placeholder="Type to Search for Medication"
                              autoComplete="off"
                              disabled={props.readOnly}
                            />
                            {medicationSearch && medicationSearch.key == problem.key && medicationSearch?.results?.length > 0 && (
                              <>                                
                                {/* <div> 
                                  <strong style={{ fontFamily: "QuicksandMedium" }}>
                                    Available Medications:{" "}
                                    <small>(Click medication below to set above)</small>
                                  </strong>
                                </div> */}
                                
                                <div
                                  style={{
                                    position: "relative",
                                  }}
                                > 
                                <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      zIndex: 100,
                                      border: "1px solid #929292",
                                      borderRadius: 6,
                                      maxHeight: 200,
                                      overflow: "auto",
                                      padding: 10,
                                      backgroundColor: 'white',
                                    }}
                                  >
                                    {medicationSearch.results.map((medication, index) => (
                                      <div
                                        key={index}
                                        className={index % 2 == 0 ? "rowEven" : "row"}
                                        style={{
                                          cursor: "pointer",
                                          padding: 3,
                                          fontFamily: "QuicksandMedium",
                                        }}
                                        onClick={() => {
                                          props.setMedicationProblems((prev)=> { 
                                            return prev.map((p) =>
                                              p.key === problem.key
                                                ? { ...p, medication_name: medication}
                                                : p
                                            )
                                          })
                                          // props.setMedicationName(medication);
                                          setMedicationSearch(null);
                                        }}
                                      >
                                        {medication}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </>)
                            }
                          </Grid>
                        </TableCell>
                        <TableCell sx={{width: '500px'}}>
                          <Grid item xs={12} className="field-container">
                            <TextField
                              id="type_of_problem"
                              size="small"
                              value={problem.type_of_problem || "Select Problem"}
                              className="field w-100"
                              sx={{width: '500px', textOverflow: 'ellipsis'}}
                              required
                              variant="outlined"
                              onChange={(event) => {
                                props.setMedicationProblems((prev)=> { 
                                  return prev.map((p) =>
                                    p.key === problem.key
                                      ? { ...p, type_of_problem: event.target.value}
                                      : p
                                  )
                                })
                                // props.setEncounterType(event.target.value);
                              }}
                              select
                              disabled={props.readOnly}
                            >
                              <MenuItem value="Select Problem" disabled>
                                Select Problem
                              </MenuItem>
                              {DRUG_THERAPY_PROBLEMS.map((problem) => (
                                <MenuItem key={problem} value={problem}>
                                  {problem}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </TableCell>
                        <TableCell>
                          <Grid item xs={12} className="field-container">
                            <TextField
                              id="resolution"
                              size="small"
                              value={problem.resolution || "Select Resolution"}
                              className="field w-100"
                              required
                              variant="outlined"
                              onChange={(event) => {
                                props.setMedicationProblems((prev)=> { 
                                  return prev.map((p) =>
                                    p.key === problem.key
                                      ? { ...p, resolution: event.target.value}
                                      : p
                                  )
                                })
                                // props.setEncounterType(event.target.value);
                              }}
                              select
                              disabled={props.readOnly}
                            >
                              <MenuItem value="Select Resolution" disabled>
                                Select Resolution
                              </MenuItem>
                              {DRUG_PROBLEM_RESOLUTIONS.map((resolution) => (
                                <MenuItem key={resolution} value={resolution}>
                                  {resolution}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </TableCell>
                        <TableCell>
                        {!props.readOnly &&
                          <Link
                            onClick={() => {
                              props.setMedicationProblems((prev)=> { 
                                return prev.filter((p) => p.key !== problem.key)
                              })
                             }
                            }
                            style={{
                              color: "black",
                            }}
                          >
                            <DeleteIcon />
                          </Link> 
                        }
                        </TableCell>
                      </TableRow>)
                    }))
                }
              </TableBody>
            </Table>
          </TableContainer>
          {
            props.medicationProblems?.length == 0 &&
              <span style={{textAlign: 'center', marginTop: '10px', width: '100%'}}> No medication related problems </span> 
          }
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EncounterMedicationProblemsPanel;
